// frontend/src/pages/PayrollGuide/index.tsx
// Thin orchestrator: builds sizing config, renders static layout + 3 sub-components.
// Content lives in content.ts, sub-components in components/, styling in styles.ts.

import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import useAI from "../../hooks/useAI";

import { STEPS, FAQS } from "./content";
import { C, FONT, buildSizeConfig } from "./styles";

import StepSection from "./components/StepSection";
import FaqAccordion from "./components/FaqAccordion";
import NexaCta from "./components/NexaCta";

interface PayrollGuideProps {
  /** When true, uses compact sizing for rendering inside a narrow side panel. */
  embeddedInPanel?: boolean;
  /** Panel close callback. Accepted for parity with the panel wrapper; the guide itself does not close. */
  onClose?: () => void;
}

export default function PayrollGuide({
  embeddedInPanel = false,
  onClose: _onClose,
}: PayrollGuideProps): React.ReactElement {
  const navigate = useNavigate();
  const { askAndOpen } = useAI();

  const sz = buildSizeConfig(embeddedInPanel);

  return (
    <div style={{ background: C.surface, minHeight: "100vh", fontFamily: FONT, color: C.text }}>
      <div style={{ maxWidth: sz.contentMaxW, margin: "0 auto", padding: sz.contentPad }}>
        {/* Back button (standalone only) */}
        {!embeddedInPanel && (
          <button
            onClick={() => navigate("/payroll/overview")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13.5,
              fontWeight: 600,
              color: C.tealInk,
              cursor: "pointer",
              background: "none",
              border: "none",
              fontFamily: FONT,
              marginBottom: 14,
              padding: 0,
            }}
          >
            <ChevronLeft size={14} /> Back to Payroll overview
          </button>
        )}

        {/* Novala brand (panel only) */}
        {embeddedInPanel && (
          <div style={{ textAlign: "center", marginBottom: 24, paddingTop: 6 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <img
                src="/logo-mark-header.png"
                alt="Novala"
                style={{
                  width: sz.logoSize,
                  height: sz.logoSize,
                  borderRadius: 10,
                  objectFit: "contain",
                  background: "#fff",
                  padding: 4,
                  boxShadow: "0 2px 8px rgba(16,26,43,0.08)",
                }}
              />
              <span
                style={{
                  fontSize: sz.brandName,
                  fontWeight: 700,
                  color: C.ink,
                  letterSpacing: "-0.02em",
                }}
              >
                Novala
              </span>
            </div>
          </div>
        )}

        {/* Hero */}
        <div
          style={{
            background: "linear-gradient(135deg, #EAF8F4, #F1F8F6)",
            border: "1px solid #D5EDE6",
            borderRadius: 18,
            padding: sz.heroPad,
            marginBottom: 24,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: sz.heroEyebrow,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: C.tealInk,
              marginBottom: 8,
            }}
          >
            Welcome to Novala payroll
          </div>
          <h1
            style={{
              fontSize: sz.heroH1,
              fontWeight: 600,
              color: C.ink,
              letterSpacing: "-0.02em",
              marginBottom: 8,
              lineHeight: 1.2,
            }}
          >
            Payroll, simplified
          </h1>
          <p
            style={{
              fontSize: sz.heroP,
              color: C.muted,
              maxWidth: 580,
              margin: "0 auto",
              lineHeight: 1.6,
            }}
          >
            Running payroll on Novala takes five steps. This guide walks you through each one, so you know what is happening, why it matters, and where to find it in the app.
          </p>
        </div>

        {/* Flow diagram (5-step preview) */}
        <div
          style={{
            background: "#fff",
            border: "1px solid " + C.line,
            borderRadius: 14,
            padding: "24px 28px",
            marginBottom: 36,
            boxShadow: "0 1px 2px rgba(16,26,43,0.04)",
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: C.faint,
              marginBottom: 18,
              textAlign: "center",
            }}
          >
            How payroll works on Novala
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, alignItems: "start" }}>
            {STEPS.map((step) => (
              <div key={step.num} style={{ textAlign: "center", position: "relative", padding: "0 4px" }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: C.teal,
                    color: "#fff",
                    display: "grid",
                    placeItems: "center",
                    margin: "0 auto 10px",
                    fontSize: 14,
                    fontWeight: 700,
                    boxShadow: "0 2px 8px rgba(21,160,140,0.28)",
                  }}
                >
                  {step.num}
                </div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: C.ink, lineHeight: 1.3 }}>
                  {step.title.replace("Set up your ", "Set ").replace("Pay your payroll taxes", "Pay tax")}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Full step sections */}
        {STEPS.map((step) => (
          <StepSection
            key={step.num}
            step={step}
            sz={sz}
            onCtaClick={() => navigate(step.route)}
          />
        ))}

        {/* FAQ */}
        <div
          style={{
            fontSize: sz.faqTitle,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: C.faint,
            margin: sz.faqMargin,
            padding: "0 4px",
          }}
        >
          Frequently asked questions
        </div>
        {FAQS.map((faq, i) => (
          <FaqAccordion key={i} faq={faq} sz={sz} />
        ))}

        {/* Nexa CTA + Contact us footer */}
        <NexaCta
          sz={sz}
          embeddedInPanel={embeddedInPanel}
          onAskAboutPayroll={() =>
            askAndOpen("Hi! I have questions about payroll on Novala. Can you help?")
          }
          onContactUs={() => askAndOpen("Hi! I need help.")}
        />
      </div>
    </div>
  );
}